// Sidebar redesign — workflow-shaped IA, Cmd-K row, live badges,
// icon-rail collapsed state, NEW pill, path-prefix active matcher.
//
// Replaces the LT-311 consolidation with the full handoff spec from
// design_handoff_sidebar (workflow groups WORK / PIPELINE / PLAYBOOKS /
// INSIGHTS, with Settings + Profile as a footer cluster and an admin
// group gated on isAdmin()).
//
// Preserved from the previous sidebar:
//   - data-tour="sidebar" on the root container.
//   - Per-item data-tour ids (deliberate map, not derived from path) so
//     the existing OnboardingTour selectors keep working for the items
//     that still exist in the new IA.
//   - Mobile drawer behaviour (lg:hidden / lg:translate-x-0). The drawer
//     locks density to 'full' on mobile because the rail variant is
//     desktop-only.
//   - Admin-gating via useAuth().isAdmin().
//
// New:
//   - Cmd-K row that opens the existing CommandPalette via
//     useCommandPalette(). Clicking the row or pressing ⌘K both open it.
//   - Live badges on Today, Inbound, Follow-ups, Campaigns, Sequences —
//     polled at 30s in src/components/sidebar/badges.ts.
//   - Icon-rail mode (60 px) with tooltips and a corner dot for badged
//     items. Toggled by the brand-row collapse button or `[`.
//   - NEW pill on Today + Sequences that auto-clears once the user has
//     visited the route ≥ 3 times (tracked in localStorage).
//
// DS rules — colors come from the DS tokens in src/index.css, via the
// Tailwind classes mirrored in tailwind.config.js (bg-surface-1,
// text-accent, bg-accent-tint, border-accent-border, etc.). No raw hex,
// no Tailwind default palette colours.

import React, { useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  BarChart3,
  Briefcase,
  Building2,
  ChevronLeft,
  ChevronRight,
  Flag,
  LifeBuoy,
  ListChecks,
  Megaphone,
  MessageSquare,
  PhoneCall,
  PhoneIncoming,
  Search,
  Settings as SettingsIcon,
  Shield,
  Sparkles,
  Ticket,
  TrendingUp,
  User,
  Users,
} from 'lucide-react';

import { useAuth } from '../context/AuthContext';
import { useCommandPalette } from './CommandPalette';
import {
  useCampaignsPausedCount,
  useFollowUpsPendingCount,
  useInboundRingingCount,
} from './sidebar/badges';
import { useSidebarDensity } from './sidebar/density';
import {
  recordSidebarVisit,
  useNewPillEligibility,
} from './sidebar/visitTracker';

interface SidebarProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

type IconComponent = React.ComponentType<{ className?: string; strokeWidth?: number | string }>;

type BadgeTone = 'warn' | 'danger' | 'info' | 'neutral';

type Badge =
  | { kind: 'count'; value: number; tone: BadgeTone }
  | { kind: 'pulse'; value: number; tone: BadgeTone }
  | { kind: 'pill'; text: string; tone: BadgeTone };

interface NavItem {
  id: string;
  label: string;
  path: string;
  icon: IconComponent;
  /** Set explicitly per item — never derived from `path`. */
  dataTour?: string;
  /** Renders a small "NEW" pill (auto-clears once recordSidebarVisit is
   *  ≥ NEW_PILL_HIDE_THRESHOLD for the path). */
  showNewPill?: boolean;
  badge?: Badge;
}

interface NavGroup {
  id: string;
  header: string;
  items: NavItem[];
}

// Path-prefix-based active matcher. Replaces the per-path special cases
// that used to live inline. Order matters — most-specific first wins.
const ACTIVE_PREFIXES: Array<{ prefix: string; itemPath: string }> = [
  { prefix: '/campaigns', itemPath: '/campaigns' },
  { prefix: '/contacts', itemPath: '/contacts' },
  { prefix: '/contact-lists', itemPath: '/contact-lists' },
  { prefix: '/accounts', itemPath: '/accounts' },
  { prefix: '/deals', itemPath: '/deals' },
  { prefix: '/calls', itemPath: '/calls' },
  { prefix: '/runs/', itemPath: '/calls' }, // /runs/:id/calls/:id transcripts route to Calls
  { prefix: '/inbound-calls', itemPath: '/inbound-calls' },
  { prefix: '/follow-ups', itemPath: '/follow-ups' },
  // LT-411: Agents nav reverts to /agents (LT-399 D-05 reversal). The
  // legacy /create-agent route still resolves but redirects, so a stale
  // bookmark resolves to the right sidebar item.
  { prefix: '/agents', itemPath: '/agents' },
  { prefix: '/create-agent', itemPath: '/agents' },
  { prefix: '/today', itemPath: '/today' },
  { prefix: '/analytics', itemPath: '/analytics' },
  { prefix: '/dashboard', itemPath: '/dashboard' },
  { prefix: '/settings', itemPath: '/settings' },
  { prefix: '/profile', itemPath: '/profile' },
  { prefix: '/admin/feedbacks', itemPath: '/admin/feedbacks' },
  { prefix: '/admin/tickets', itemPath: '/admin/tickets' },
  { prefix: '/admin', itemPath: '/admin' },
];

export function isActivePath(itemPath: string, pathname: string): boolean {
  if (pathname === itemPath) return true;
  // Match the longest applicable prefix so /campaigns/abc routes to
  // /campaigns and /admin/feedbacks beats /admin. The boundary is `/` so
  // /contacts doesn't bleed into /contact-lists.
  let bestPrefixLen = 0;
  let bestItemPath = '';
  for (const rule of ACTIVE_PREFIXES) {
    const matches =
      pathname === rule.prefix ||
      (rule.prefix.endsWith('/')
        ? pathname.startsWith(rule.prefix)
        : pathname.startsWith(rule.prefix + '/'));
    if (matches && rule.prefix.length > bestPrefixLen) {
      bestPrefixLen = rule.prefix.length;
      bestItemPath = rule.itemPath;
    }
  }
  return bestItemPath === itemPath;
}

interface BuildIaArgs {
  inboundRinging: number;
  followUpsPending: number;
  campaignsPaused: number;
}

function buildIa(args: BuildIaArgs): NavGroup[] {
  const {
    inboundRinging,
    followUpsPending,
    campaignsPaused,
  } = args;

  return [
    // Standalone Dashboard at top — empty header renders just the item.
    {
      id: 'top',
      header: '',
      items: [
        { id: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: BarChart3, dataTour: 'dashboard' },
      ],
    },
    {
      id: 'playbooks',
      header: 'Playbooks',
      items: [
        // LT-411: reverts LT-399 D-05; nav points at the catalogue.
        { id: 'agents', label: 'Agents', path: '/agents', icon: Sparkles, dataTour: 'agents' },
        // LT-532: Sequences nav item removed — the builder surface is retired.
        {
          id: 'campaigns',
          label: 'Campaigns',
          path: '/campaigns',
          icon: Megaphone,
          dataTour: 'campaigns',
          badge:
            campaignsPaused > 0
              ? { kind: 'pill', text: `${campaignsPaused} paused`, tone: 'warn' }
              : undefined,
        },
      ],
    },
    {
      id: 'pipeline',
      header: 'Pipeline',
      items: [
        { id: 'accounts', label: 'Accounts', path: '/accounts', icon: Building2, dataTour: 'accounts' },
        { id: 'contact-lists', label: 'Contact lists', path: '/contact-lists', icon: ListChecks, dataTour: 'contact-lists' },
        { id: 'contacts', label: 'Contacts', path: '/contacts', icon: Users, dataTour: 'contacts' },
        { id: 'deals', label: 'Deals', path: '/deals', icon: Briefcase, dataTour: 'deals' },
      ],
    },
    {
      id: 'work',
      header: 'Work',
      items: [
        {
          id: 'follow-ups',
          label: 'Follow-ups',
          path: '/follow-ups',
          icon: Flag,
          dataTour: 'follow-ups',
          badge:
            followUpsPending > 0
              ? { kind: 'count', value: followUpsPending, tone: 'warn' }
              : undefined,
        },
        { id: 'calls', label: 'Calls', path: '/calls', icon: PhoneCall, dataTour: 'calls' },
        {
          id: 'inbound',
          label: 'Inbound',
          path: '/inbound-calls',
          icon: PhoneIncoming,
          dataTour: 'inbound-calls',
          badge:
            inboundRinging > 0
              ? { kind: 'pulse', value: inboundRinging, tone: 'danger' }
              : undefined,
        },
      ],
    },
    {
      id: 'insights',
      header: 'Insights',
      items: [
        { id: 'analytics', label: 'Analytics', path: '/analytics', icon: TrendingUp, dataTour: 'analytics' },
      ],
    },
  ];
}

// CCT-7: Support + Feedback were reachable only by URL or the avatar menu —
// surface them in the sidebar footer for every user. The data-tour ids also
// wire up the existing onboarding-tour steps that target them.
const FOOTER_ITEMS: NavItem[] = [
  { id: 'settings', label: 'Settings', path: '/settings', icon: SettingsIcon, dataTour: 'settings' },
  { id: 'support', label: 'Support', path: '/support', icon: LifeBuoy, dataTour: 'support' },
  { id: 'feedback', label: 'Feedback', path: '/feedback', icon: MessageSquare, dataTour: 'feedback' },
  { id: 'profile', label: 'Profile', path: '/profile', icon: User },
];

const ADMIN_GROUP: NavGroup = {
  id: 'admin',
  header: 'Admin',
  items: [
    { id: 'admin', label: 'Admin Panel', path: '/admin', icon: Shield },
    { id: 'admin-feedbacks', label: 'Feedbacks', path: '/admin/feedbacks', icon: MessageSquare },
    { id: 'admin-tickets', label: 'Tickets', path: '/admin/tickets', icon: Ticket },
  ],
};

// ─── Atoms ───────────────────────────────────────────────────────────

const TONE_BADGE_CLASS: Record<BadgeTone, string> = {
  warn: 'bg-warn-tint text-warn-text border-warn-border',
  danger: 'bg-danger-tint text-danger-text border-danger-border',
  info: 'bg-info-tint text-info-text border-info-border',
  neutral: 'bg-surface-2 text-slate-300 border-white/[0.08]',
};

const TONE_DOT_CLASS: Record<BadgeTone, string> = {
  warn: 'bg-warn shadow-[0_0_6px_var(--warn)]',
  danger: 'bg-danger shadow-[0_0_6px_var(--danger)] animate-pulse',
  info: 'bg-info',
  neutral: 'bg-slate-400',
};

const TONE_PULSE_DOT_CLASS: Record<BadgeTone, string> = {
  warn: 'bg-warn',
  danger: 'bg-danger',
  info: 'bg-info',
  neutral: 'bg-slate-400',
};

interface BadgePillProps {
  badge: Badge;
}

const BadgePill: React.FC<BadgePillProps> = ({ badge }) => {
  if (badge.kind === 'pulse') {
    return (
      <span
        className={`inline-flex items-center gap-1 px-1.5 h-4 rounded-full text-[10px] font-semibold border ${TONE_BADGE_CLASS[badge.tone]}`}
        data-testid="sidebar-badge-pulse"
      >
        <span
          className={`w-1.5 h-1.5 rounded-full animate-pulse ${TONE_PULSE_DOT_CLASS[badge.tone]}`}
          aria-hidden
        />
        {badge.value}
      </span>
    );
  }
  if (badge.kind === 'pill') {
    return (
      <span
        className={`inline-flex items-center px-1.5 h-4 rounded-full text-[10px] font-semibold border ${TONE_BADGE_CLASS[badge.tone]}`}
        data-testid="sidebar-badge-pill"
      >
        {badge.text}
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center justify-center px-1.5 min-w-[16px] h-4 rounded-full text-[10px] font-semibold border ${TONE_BADGE_CLASS[badge.tone]}`}
      data-testid="sidebar-badge-count"
    >
      {badge.value}
    </span>
  );
};

interface SidebarItemProps {
  item: NavItem;
  active: boolean;
  density: 'full' | 'rail';
  showNewPill: boolean;
  onNavigate: () => void;
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  item,
  active,
  density,
  showNewPill,
  onNavigate,
}) => {
  const Icon = item.icon;
  const isRail = density === 'rail';

  // Minimal active-state per the spec: 10% accent-tint background + 3px
  // left accent rule (rendered with the ::before workaround via padding-l
  // and an absolutely-positioned span). No big gradient + glow recipe.
  const baseClass = isRail
    ? 'group relative flex items-center justify-center w-9 h-9 mx-auto my-0.5 rounded-ds-md transition-colors'
    : 'group relative flex items-center gap-2.5 py-1.5 pl-3 pr-2 rounded-ds-md transition-colors';

  const stateClass = active
    ? 'bg-accent-tint text-slate-50'
    : 'text-slate-300 hover:bg-surface-2 hover:text-slate-50';

  return (
    <Link
      to={item.path}
      data-tour={item.dataTour}
      onClick={onNavigate}
      className={`${baseClass} ${stateClass}`}
      title={isRail ? item.label : undefined}
      aria-current={active ? 'page' : undefined}
    >
      {/* 3px accent rule on active, both densities. */}
      {active ? (
        <span
          aria-hidden
          className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-accent"
        />
      ) : null}
      <Icon
        className={`w-4 h-4 flex-shrink-0 transition-colors ${
          active ? 'text-accent' : 'text-slate-400 group-hover:text-accent'
        }`}
        strokeWidth={2}
      />
      {!isRail ? (
        <>
          <span className="flex-1 text-sm font-medium truncate flex items-center gap-1.5">
            {item.label}
            {showNewPill ? (
              <span className="inline-flex items-center px-1 h-3.5 rounded-sm text-[8px] font-bold tracking-wider uppercase bg-info-tint text-info-text border border-info-border">
                NEW
              </span>
            ) : null}
          </span>
          {item.badge ? <BadgePill badge={item.badge} /> : null}
        </>
      ) : (
        item.badge ? (
          <span
            aria-hidden
            className={`absolute top-1 right-1 w-1.5 h-1.5 rounded-full ${TONE_DOT_CLASS[item.badge.tone]}`}
            data-testid="sidebar-rail-dot"
          />
        ) : null
      )}
      {/* Rail tooltip (keyboard-focus-visible too). title= covers mouse;
          screen readers get the link text via aria-label fallback below. */}
      {isRail ? <span className="sr-only">{item.label}</span> : null}
    </Link>
  );
};

interface CmdKRowProps {
  density: 'full' | 'rail';
  onOpen: () => void;
}

const CmdKRow: React.FC<CmdKRowProps> = ({ density, onOpen }) => {
  const isMac =
    typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform || '');
  const shortcut = isMac ? '⌘K' : 'Ctrl K';

  if (density === 'rail') {
    return (
      <button
        type="button"
        onClick={onOpen}
        title={`Search · ${shortcut}`}
        className="flex items-center justify-center w-9 h-9 mx-auto rounded-ds-md text-slate-400 hover:text-slate-50 hover:bg-surface-2 transition-colors"
      >
        <Search className="w-4 h-4" strokeWidth={2} />
        <span className="sr-only">Search ({shortcut})</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full flex items-center gap-2.5 h-8 px-2.5 rounded-ds-md bg-surface-2 hover:bg-surface-3 border border-white/[0.05] hover:border-white/[0.10] text-slate-400 hover:text-slate-200 text-xs transition-colors"
      data-testid="sidebar-cmdk-row"
    >
      <Search className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
      <span className="flex-1 text-left">Search…</span>
      <span className="inline-flex items-center h-4 px-1.5 rounded text-[10px] font-mono font-medium bg-surface-3 border border-white/[0.08] text-slate-300">
        {shortcut}
      </span>
    </button>
  );
};

// ─── Sidebar ─────────────────────────────────────────────────────────

const Sidebar: React.FC<SidebarProps> = ({ isOpen, setIsOpen }) => {
  const location = useLocation();
  const { isAdmin } = useAuth();
  const { openPalette } = useCommandPalette();
  const { density, toggleDensity } = useSidebarDensity();

  // The mobile drawer is always full-density — the rail variant assumes
  // a sticky desktop column. Effective density only matters at lg+.
  const effectiveDensity = density;

  // Track every visit to a sidebar route so the "NEW" pill auto-hides
  // for users who've already used the destination a few times.
  useEffect(() => {
    recordSidebarVisit(location.pathname);
  }, [location.pathname]);

  // The active item should treat /campaigns/abc as /campaigns. Computed
  // via path-prefix matching, not per-path special cases.
  const inboundRinging = useInboundRingingCount();
  const followUpsPending = useFollowUpsPendingCount();
  const campaignsPaused = useCampaignsPausedCount();

  const groups = useMemo(
    () =>
      buildIa({
        inboundRinging,
        followUpsPending,
        campaignsPaused,
      }),
    [inboundRinging, followUpsPending, campaignsPaused],
  );

  const isPillEligible = useNewPillEligibility();

  const handleNavigate = () => setIsOpen(false);

  const renderItem = (item: NavItem) => (
    <SidebarItem
      key={item.id}
      item={item}
      active={isActivePath(item.path, location.pathname)}
      density={effectiveDensity}
      showNewPill={!!item.showNewPill && isPillEligible(item.path)}
      onNavigate={handleNavigate}
    />
  );

  const renderGroup = (group: NavGroup) => (
    <div key={group.id} className="mb-3">
      {effectiveDensity === 'rail' ? (
        <div className="h-px mx-2 my-2 bg-white/[0.05]" aria-hidden />
      ) : group.header ? (
        <h3 className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest mb-1.5 px-3">
          {group.header}
        </h3>
      ) : null}
      <div className="space-y-0.5">{group.items.map(renderItem)}</div>
    </div>
  );

  // Width inverts between full / rail. Mobile drawer always uses full
  // (the rail mode is a desktop convenience).
  const widthClass =
    effectiveDensity === 'rail'
      ? 'w-[60px]'
      : 'w-[232px]';

  return (
    <>
      {/* Mobile backdrop */}
      <div
        className="lg:hidden fixed inset-0 bg-black/60 z-40 transition-opacity duration-300"
        style={{
          opacity: isOpen ? '1' : '0',
          pointerEvents: isOpen ? 'auto' : 'none',
        }}
        onClick={() => setIsOpen(false)}
      />

      {/* Sidebar shell */}
      <div
        className={`fixed top-0 left-0 h-full z-50 transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 lg:z-10`}
      >
        <div
          className={`h-screen bg-surface-1 border-r border-white/[0.08] shadow-ds-e2 flex flex-col transition-[width] duration-200 ${widthClass}`}
          data-tour="sidebar"
          data-sidebar-density={effectiveDensity}
        >
          {/* Brand row */}
          <div
            className={`flex items-center h-16 border-b border-white/[0.08] flex-shrink-0 ${
              effectiveDensity === 'rail' ? 'justify-center px-2' : 'justify-between px-4'
            }`}
          >
            <div className="flex items-center gap-2 min-w-0">
              {/* LT-497 — Agent Noda brand mark. The mark already carries
                  its painted black plate, so the previous accent-coloured
                  container chrome is gone; the wordmark sits beside it
                  in full density mode, and the mark stands alone in rail
                  mode. */}
              <img
                src={`${process.env.PUBLIC_URL ?? ''}/brand/agent-noda-mark.svg`}
                alt="Agent Noda"
                className="w-7 h-7 flex-shrink-0 rounded-ds-md"
              />
              {effectiveDensity === 'full' ? (
                <h1 className="text-base font-semibold text-slate-50 tracking-tight truncate">
                  Agent Noda
                </h1>
              ) : null}
            </div>
            {effectiveDensity === 'full' ? (
              <button
                type="button"
                onClick={toggleDensity}
                aria-label="Collapse sidebar"
                title="Collapse (])"
                className="hidden lg:inline-flex items-center justify-center w-7 h-7 rounded-ds-sm text-slate-400 hover:text-slate-50 hover:bg-surface-2 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" strokeWidth={2} />
              </button>
            ) : null}
            {/* Mobile close (only present on mobile drawer, never in rail) */}
            <button
              onClick={() => setIsOpen(false)}
              aria-label="Close sidebar"
              className="lg:hidden text-slate-400 hover:text-slate-50 transition-colors p-1.5 hover:bg-surface-2 rounded-ds-sm"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Rail-mode expand button (sits below the brand) */}
          {effectiveDensity === 'rail' ? (
            <button
              type="button"
              onClick={toggleDensity}
              aria-label="Expand sidebar"
              title="Expand (])"
              className="hidden lg:inline-flex self-center items-center justify-center w-7 h-7 my-2 rounded-ds-sm text-slate-400 hover:text-slate-50 hover:bg-surface-2 transition-colors"
            >
              <ChevronRight className="w-4 h-4" strokeWidth={2} />
            </button>
          ) : null}

          {/* Cmd-K row */}
          <div className={`flex-shrink-0 ${effectiveDensity === 'rail' ? 'px-2 py-1.5' : 'px-3 py-2'}`}>
            <CmdKRow density={effectiveDensity} onOpen={openPalette} />
          </div>

          {/* Nav body */}
          <div className="flex-1 px-2 py-2 overflow-y-auto custom-scrollbar">
            <nav role="navigation" aria-label="Primary">
              {groups.map(renderGroup)}
              {isAdmin() ? renderGroup(ADMIN_GROUP) : null}
            </nav>
          </div>

          {/* Footer cluster */}
          <div className="flex-shrink-0 border-t border-white/[0.08] px-2 py-2">
            <div className="space-y-0.5">{FOOTER_ITEMS.map(renderItem)}</div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
